import { RingBuffer } from "./ring-buffer";
import type { NetworkEntry } from "./types";

const CAPACITY = 50;
const EXCERPT_MAX = 2048;

export interface NetworkCapture {
  snapshot(): NetworkEntry[];
  count(): number;
  /** Exclut du tampon les appels du widget lui-même vers le tracker. */
  ignoreUrl(url: string): void;
  stop(): void;
}

let active: NetworkCapture | null = null;

function resolveUrl(input: string): string {
  try {
    return new URL(input, window.location.href).href;
  } catch {
    return input;
  }
}

export function startNetworkCapture(): NetworkCapture {
  if (active) return active;

  const buffer = new RingBuffer<NetworkEntry>(CAPACITY);
  const ignored = new Set<string>();

  const isIgnored = (url: string) => {
    for (const prefix of ignored) {
      if (url.startsWith(prefix)) return true;
    }
    return false;
  };

  // --- fetch ---
  const originalFetch = window.fetch.bind(window);
  const patchedFetch: typeof window.fetch = async (input, init) => {
    const url = resolveUrl(
      typeof input === "string" ? input : input instanceof URL ? input.href : input.url,
    );
    const method = (
      init?.method ?? (input instanceof Request ? input.method : "GET")
    ).toUpperCase();
    const start = performance.now();
    const timestamp = new Date().toISOString();

    if (isIgnored(url)) return originalFetch(input as RequestInfo, init);

    try {
      const response = await originalFetch(input as RequestInfo, init);
      const entry: NetworkEntry = {
        method,
        url,
        status: response.status,
        durationMs: Math.round(performance.now() - start),
        timestamp,
      };
      if (response.status >= 400) {
        try {
          const text = await response.clone().text();
          if (text) entry.responseExcerpt = text.slice(0, EXCERPT_MAX);
        } catch {
          // corps illisible (stream déjà consommé, opaque…) — tant pis
        }
      }
      buffer.push(entry);
      return response;
    } catch (err) {
      buffer.push({
        method,
        url,
        status: null,
        durationMs: Math.round(performance.now() - start),
        timestamp,
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  };
  window.fetch = patchedFetch;

  // --- XMLHttpRequest ---
  const XHR = XMLHttpRequest.prototype;
  const originalOpen = XHR.open;
  const originalSend = XHR.send;

  interface XhrMeta {
    method: string;
    url: string;
    start: number;
    timestamp: string;
  }
  const metas = new WeakMap<XMLHttpRequest, XhrMeta>();

  XHR.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
    metas.set(this, {
      method: method.toUpperCase(),
      url: resolveUrl(String(url)),
      start: 0,
      timestamp: "",
    });
    // @ts-expect-error — on relaie la signature complète (async, user, password)
    return originalOpen.call(this, method, url, ...rest);
  };

  XHR.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
    const meta = metas.get(this);
    if (meta && !isIgnored(meta.url)) {
      meta.start = performance.now();
      meta.timestamp = new Date().toISOString();
      this.addEventListener("loadend", () => {
        const entry: NetworkEntry = {
          method: meta.method,
          url: meta.url,
          status: this.status === 0 ? null : this.status,
          durationMs: Math.round(performance.now() - meta.start),
          timestamp: meta.timestamp,
        };
        if (this.status === 0) {
          entry.error = "Erreur réseau";
        } else if (this.status >= 400) {
          try {
            if (this.responseType === "" || this.responseType === "text") {
              const text = this.responseText;
              if (text) entry.responseExcerpt = text.slice(0, EXCERPT_MAX);
            }
          } catch {
            // responseText inaccessible selon le responseType
          }
        }
        buffer.push(entry);
      });
    }
    return originalSend.call(this, body);
  };

  active = {
    snapshot: () => buffer.snapshot(),
    count: () => buffer.size,
    ignoreUrl: (url) => ignored.add(url),
    stop() {
      window.fetch = originalFetch;
      XHR.open = originalOpen;
      XHR.send = originalSend;
      active = null;
    },
  };
  return active;
}
